import { FillableWidgetList } from '@framework/engine/schema/form/fillable/FillableWidgetList.tsx';
import { WidgetScopeContextProvider } from '@framework/engine/schema/form/fillable/WidgetScopeContext.tsx';
import { type IEngineSection } from '@framework/engine/schema/sections/IEngineSection.ts';
import { type Widget } from '@framework/engine/schema/widgets/Widget.ts';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useModuleFormSectionVisible } from '../form/sections-monitor/ModuleFormSectionsMonitorContext.tsx';

export interface IFillableFormProps {
  section: IEngineSection;
  readonly?: boolean;
  /** Permette di gestire dinamicamente il readonly per ogni singolo widget */
  isReadonly?: (widget: Widget) => boolean;
}
/**
 * Anchor tecnico a ingombro nullo: resta nel flusso (offsetParent != null)
 * così \`focusNextElement\` lo trova per id, ma non occupa spazio verticale
 * tra le sezioni. Non usare display:none (romperebbe la navigazione).
 */
const SectionAnchor = styled.button`
  display: block;
  height: 0;
  width: 0;
  padding: 0;
  margin: 0;
  border: 0;
  overflow: hidden;
`;

/**
 * Componente con il form compilabile di _una_ singola sezione.
 */
export function FillableForm(props: IFillableFormProps) {
  const { section } = props;
  const { t } = useTranslation();
  const visible = useModuleFormSectionVisible(section);

  if (!visible) return null;

  return (
    <WidgetScopeContextProvider prefix={`${section.id}.`}>
      {/*
        Anchor per navigare direttamente ad una sezione.
        Reso a ingombro nullo per non aggiungere spazio tra le sezioni; resta un
        <button> nel flusso (offsetParent != null) perché è il target di
        `focusNextElement`, che mette il focus sull'elemento successivo.
        - tabIndex=-1 per evitare che l'elemento sia focusabile con il tab "manuale"
      */}
      <SectionAnchor type="button" id={section.id} tabIndex={-1}>
        <span className="sr-only">
          {t(
            'fillable-form.sezione-della-compilazione',
            'Sezione della compilazione',
          )}
        </span>
      </SectionAnchor>
      <FillableWidgetList
        widgets={section.ultimaVersione?.elementi?.children ?? []}
        readonly={props.readonly}
        isReadonly={props.isReadonly}
      />
    </WidgetScopeContextProvider>
  );
}
